import moment from 'moment';
import { findBinIndex } from './chart.tsx';
import {
    Annotation, EditableAnnotation,
    SubjectCircle, ConnectorElbow, 
    ConnectorLine, ConnectorEndDot, Note 
} from 'react-annotation'
  
import pure from 'react-recompose/pure';
import classNames from 'classnames';

import binStyleNames from './styles.less';

const playheadColor = '#FCBA03';
const normalTimeFormat = 'MM/DD/YYYY hh:mm:ss A z';
const millisecondTimeFormat = 'MM/DD/YYYY hh:mm:ss:SSS A z';

const clamp = (x, min, max) => Math.max(min, Math.min(max, x));
const xyToTranslateStr = (x, y) => `translate(${x.toFixed(1)},${y.toFixed(1)})`;

export const Bins = pure(function renderBars({
    id,
    clipPath,
    transform,
    onTouchMove,
    onTouchStart,
    onBinClick,
    onWheel,
    pointerEvents,
    isWheelScrolling,
    numBins = 0,
    startBin = 0,
    endBin = numBins,
    timeDomain = [],
    binCounts = [],
    binValues = [],
    maskedCounts = [],
    maskedValues = [],
    filteredCounts = [],
    filteredValues = [],
    styleNames = {},
    selectedBin = -1,
    highlightedBin = -1,
    minTime,
    timeScale = () => 0,
    valueScale = () => 0,
    padding = 0,
    width,
    height,
    minBarHeight = 0,
    timebar,
    ...props
}) {
    const bins = [];
    const gradientDefs = [];
    const binRect = Object.create(null);
    const numBarsToRender = Math.max(0, endBin - startBin);

    const fillProps = Object.create(null);
    fillProps.height = height;
    fillProps.padding = padding;
    fillProps.valueScale = valueScale;
    fillProps.styleNames = styleNames;

    let highlightedBinRect = null;
    let playheadBinRect = null;
    let annotation = null;
    const [minWindowTime, maxWindowTime] = timeDomain.map((x) => +x);
    const originX = timeScale(minTime);

    for (let i = -1; ++i < numBarsToRender; ) {
        const binID = startBin + i;
        const count = binCounts[binID] || 0;
        if (count <= 0) {
            continue;
        }

        const t0 = +binValues[binID * 2];
        const t1 = +binValues[binID * 2 + 1];
        if (t1 < minWindowTime || t0 > maxWindowTime) {
            continue;
        }

        //======================

        const binY = valueScale(count) || 0;
        const binX0 = Math.min(timeScale(t0), timeScale(t1)) - originX;
        const binX1 = Math.max(timeScale(t0), timeScale(t1)) - originX;

        // const binX = binX0;
        // if (binX > width) {
        //     continue; //TODO should be unnecessary
        // }

        // overflow heights get clipped stroke effect
        // low heights are set to 2px minimum
        const binHeight = Math.ceil(clamp(height - binY - padding, 2, height + 2));

        //======================

        let maskedBinID = findBinIndex(t0 + 1, maskedValues);
        let filteredBinID = findBinIndex(t0 + 1, filteredValues);
    
        let binFill = '#EEE';
        let hasFillStops = false;
        let annotationLabel = ``;
        let annotationTitle = `COUNT: ${count}`;
        let maskedBinCount = (maskedValues[maskedBinID * 2] === t0 ? maskedCounts[maskedBinID] : 0) || 0;
        let filteredBinCount = (filteredValues[filteredBinID * 2] === t0 ? filteredCounts[filteredBinID] : 0) || 0;
        if (maskedBinCount > 0 || filteredBinCount > 0) {
            annotationTitle=`TOTAL: ${count}`;
            if (filteredBinCount > 0) {
                annotationTitle = `${annotationTitle}\n VISIBLE: ${filteredBinCount}`;
            }
            if (maskedBinCount > 0) {
                annotationTitle = `${annotationTitle}\n SELECTED: ${maskedBinCount}`;
            }
            hasFillStops = true;
            binFill = `${id}-bin-${binID}-fill`;

            fillProps.id = binFill;
            fillProps.count = count;
            fillProps.binHeight = binHeight;
            fillProps.maskedBinCount = maskedBinCount;
            fillProps.filteredBinCount = filteredBinCount;

            gradientDefs.push(maskedBinCount > 0 ? filteredBinCount > 0 && (filteredBinCount > maskedBinCount)
                ? <Gradient3 key={binFill} {...fillProps}/>
                : <Gradient2Masked key={binFill} {...fillProps}/>
                : <Gradient2Filtered key={binFill} {...fillProps}/>
            );
        }

        //======================

        // const binWidth = Math.min(
        //     Math.max(1, binX1 - binX0),
        //     Math.max(1, width - binX0)
        // );
        
        binRect.x = binX0;
        binRect.y = height - binHeight;
        binRect.height = binHeight;
        binRect.width = Math.max(1, binX1 - binX0);

        const binRectSvg =
            <rect
                transform={xyToTranslateStr(binRect.x, binRect.y)}
                height={binRect.height}
                width={binRect.width.toFixed(1)}
                data-bin-id={binID}
                key={`bin-id-${binID}`}
                fill={hasFillStops ? `url(#${binFill})` : binFill}
                className={classNames({
                    [styleNames['bin']]: true,
                    [styleNames['play']]: timebar.mode,
                    [styleNames['selected-bin']]: binID === selectedBin,
                    [styleNames['highlighted-bin']]: highlightedBin === binID
                })}
            />;

        if ((highlightedBin !== binID) || isWheelScrolling) {
            bins.push(binRectSvg);
        } else {
            highlightedBinRect = binRectSvg;
            playheadBinRect = timebar.mode &&
                <rect
                    transform={xyToTranslateStr(binRect.x - 1, 0)}
                    width={(binRect.width + 2).toFixed(1)}
                    height={height}
                    data-bin-id={binID}
                    key={`bin-id-${binID}-playhead`}
                    fill={playheadColor}
                    className={classNames(styleNames['bin'], styleNames['playhead-bin'])}
                />;

            const annotationPadding = 5;
            const annotationOffset = 50;
            const estLabelWidth = 120;
            const tooltipHeight = 50;
            // const tooltipHeight = 50 + (maskedBinCount > 0 ? 10 : 0) + (filteredBinCount > 0 ? 10 : 0);
            const direction =  width - binRect.x - originX - (binRect.width / 2) > annotationOffset + estLabelWidth + annotationOffset ? 1  : -1;
            
            const annotationY = Math.round(Math.max(binRect.y, 0));
            const annotationX = Math.round(binRect.x + (binRect.width / 2));
            const ny = Math.round(Math.max(height * 0.25 + tooltipHeight, annotationPadding));
            const ry = Math.round(Math.max(height * 0.25 + tooltipHeight - annotationPadding, annotationPadding) + 7);

            const annotationTimeFormat = (t1 - t0) > 999 ? normalTimeFormat : millisecondTimeFormat;

            annotationLabel = `${annotationLabel}${
                moment.utc(t0).format(annotationTimeFormat)}\n ${
                moment.utc(t1).format(annotationTimeFormat)}`;

            annotation =
                <g className={styleNames['annotation']}>
                    <Annotation                
                        x={annotationX}
                        y={annotationY}
                        ny={ny}
                        wrapSplitter={/[\t\n\r]\s+/}
                        dx={ direction * annotationOffset }
                        color={"#333"}     
                        title={annotationTitle}
                        label={annotationLabel}
                        padding={5}
                    >
                        <ConnectorElbow >
                            <ConnectorEndDot />
                        </ConnectorElbow>
                        <Note lineType="horizontal" align="top" wrap={10} />
                    </Annotation>
                </g>;
        }
    }

    return (
        <g
            id={id}
            clipPath={clipPath}
            width={width}
            height={height}
            transform={transform}
        >
            <g
                className={binStyleNames['bin-container']}
                transform={xyToTranslateStr(originX, 0)}
                onMouseMove={onTouchMove}
                onTouchMove={onTouchMove}
                onMouseDown={onTouchStart}
                onTouchStart={onTouchStart}
                onWheel={onWheel}
                onClick={onBinClick}
                pointerEvents={pointerEvents}>
                {gradientDefs.length && (<defs>{gradientDefs}</defs>) || undefined}
                {bins}
                {highlightedBinRect}
                {playheadBinRect}
                {annotation}
            </g>
        </g>
    );
});

function Gradient2Masked({ id, styleNames, count, maskedBinCount, valueScale, binHeight, height, padding }) {
    const stopHeight = clamp(height - (valueScale(maskedBinCount) || 0) - padding, 0, binHeight);
    const scaledPcnt = binHeight ? 100 - (100 * (stopHeight / binHeight)) : 0;
    return (
        <linearGradient id={id} key={id} x1="0" x2="0" y1="0" y2="1">
            <stop key='global-stop-1' className={styleNames['global-stop']} offset="0%"/>
            <stop key='global-stop-2' className={styleNames['global-stop']} offset={`${scaledPcnt}%`}/>
            <stop key='masked-stop-1' className={styleNames['masked-stop']} offset={`${scaledPcnt}%`}/>
            <stop key='masked-stop-2' className={styleNames['masked-stop']} offset="100%"/>
        </linearGradient>
    );
}

function Gradient2Filtered({ id, styleNames, count, filteredBinCount, valueScale, binHeight, height, padding }) {
    const stopHeight = clamp(height - (valueScale(filteredBinCount) || 0) - padding, 0, binHeight);
    const scaledPcnt = binHeight ? 100 - (100 * (stopHeight / binHeight)) : 0;

    return (
        <linearGradient id={id} key={id} x1="0" x2="0" y1="0" y2="1">
            <stop key='global-stop-1' className={styleNames['global-stop']} offset="0%"/>
            <stop key='global-stop-2' className={styleNames['global-stop']} offset={`${scaledPcnt}%`}/>
            <stop key='filtered-stop-1' className={styleNames['filtered-stop']} offset={`${scaledPcnt}%`}/>
            <stop key='filtered-stop-2' className={styleNames['filtered-stop']} offset="100%"/>
        </linearGradient>
    );
}

function Gradient3({ id, styleNames, count, maskedBinCount, filteredBinCount, valueScale, binHeight, height, padding }) {
    
    const maskedHeight = clamp(height - (valueScale(maskedBinCount) || 0) - padding, 0, binHeight);
    const maskedPcnt = binHeight ? 100 - (100 * (maskedHeight / binHeight)) : 0;
    
    const filterHeight = clamp(height - (valueScale(filteredBinCount) || 0) - padding, 0, binHeight);
    const filterPcnt = binHeight ? 100 - (100 * (filterHeight / binHeight)) : 0;
    
    return (
        <linearGradient id={id} key={id} x1="0" x2="0" y1="0" y2="1">
            <stop key='global-stop-1' className={styleNames['global-stop']} offset="0%"/>
            <stop key='global-stop-2' className={styleNames['global-stop']} offset={`${filterPcnt}%`}/>
            <stop key='filtered-stop-1' className={styleNames['filtered-stop']} offset={`${filterPcnt}%`}/>
            <stop key='filtered-stop-2' className={styleNames['filtered-stop']} offset={`${maskedPcnt}%`}/>
            <stop key='masked-stop-1' className={styleNames['masked-stop']} offset={`${maskedPcnt}%`}/>
            <stop key='masked-stop-2' className={styleNames['masked-stop']} offset="100%"/>
        </linearGradient>
    );
}
